import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Camera,
  Check,
  Clipboard,
  Flame,
  History,
  KeyRound,
  Layers3,
  Medal,
  Palette,
  Percent,
  ShieldAlert,
  Sparkles,
  Target,
  Trash2,
  TrendingDown,
  Trophy,
  UserRound,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { getGameHistory, getStats } from "../../lib/api";
import {
  AVATAR_IDS,
  CARD_THEMES,
  type AccountStats,
  type AvatarId,
  type CardTheme,
  type GameHistoryEntry,
  type ProtocolError,
} from "../../types/protocol";
import { Avatar } from "../ui/Avatar";
import { AvatarCropper } from "../ui/AvatarCropper";
import { Brand } from "../ui/Brand";

type ProfileAction = "identity" | "appearance" | "photo" | "password" | "delete";

const avatarLabels: Record<AvatarId, string> = {
  aurora: "Aurore",
  comete: "Comète",
  nebuleuse: "Nébuleuse",
  orage: "Orage",
  prisme: "Prisme",
  quartz: "Quartz",
  recif: "Récif",
  solstice: "Solstice",
};

const themeLabels: Record<CardTheme, string> = {
  classic: "Classique",
  sunset: "Couchant",
  lagoon: "Lagon",
  ember: "Braise",
  violet: "Violet",
};

const profileMessages: Record<string, string> = {
  INVALID_CREDENTIALS: "Mot de passe incorrect.",
  WEAK_PASSWORD: "Ce mot de passe est trop fragile, allonge-le un peu.",
  HANDLE_UNAVAILABLE: "Ce pseudo n’est plus disponible, essaie une variante.",
  VALIDATION_ERROR: "Certaines informations ne sont pas valides.",
  RATE_LIMITED: "Trop de tentatives. Patiente un instant avant de réessayer.",
  UNAUTHENTICATED: "Ta session a expiré. Reconnecte-toi pour continuer.",
  ACCOUNTS_DISABLED: "Les comptes sont désactivés sur ce serveur.",
  NETWORK_ERROR: "Le serveur est injoignable. Vérifie ta connexion.",
};

/** Le serveur refuse sur les octets, pas sur le type annoncé : chaque cas a son mot. */
const photoMessages: Record<string, string> = {
  IMAGE_TOO_LARGE: "Cette photo est trop lourde. Choisis une image plus légère.",
  UNSUPPORTED_IMAGE_FORMAT:
    "Ce format d’image n’est pas pris en charge. Essaie un JPEG, un PNG ou un WebP.",
  INVALID_IMAGE: "Cette image n’a pas pu être lue. Essaie une autre photo.",
  AVATAR_NOT_FOUND: "Cette photo n’existe plus. Réessaie depuis le début.",
  RATE_LIMITED: "Trop de photos envoyées. Réessaie dans quelques minutes.",
};

const describeFailure = (action: ProfileAction, error: ProtocolError): string =>
  (action === "photo" ? photoMessages[error.code] : undefined) ??
  profileMessages[error.code] ??
  error.message;

const longDate = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" });
const shortDate = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });

const formatDate = (value: string, formatter: Intl.DateTimeFormat) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? "—" : formatter.format(new Date(parsed));
};

const placementLabel = (placement: number) => (placement === 1 ? "1er" : `${placement}e`);

const DEFAULT_MIN_PASSWORD = 8;

interface ProfileScreenProps {
  onClose: () => void;
}

interface StatTileProps {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
}

const StatTile = ({ icon, label, value, hint }: StatTileProps) => (
  <li className="stat-tile">
    <span className="stat-tile__icon" aria-hidden="true">{icon}</span>
    <span className="stat-tile__body">
      <small>{label}</small>
      <strong>{value}</strong>
      {hint && <span>{hint}</span>}
    </span>
  </li>
);

export const ProfileScreen = ({ onClose }: ProfileScreenProps) => {
  const {
    user,
    serverConfig,
    error,
    pending,
    updateProfile,
    uploadAvatarPhoto,
    removeAvatarPhoto,
    changePassword,
    deleteAccount,
    dismissError,
  } = useAuth();

  const [stats, setStats] = useState<AccountStats | null>(null);
  const [history, setHistory] = useState<GameHistoryEntry[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [dataFailed, setDataFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [draftAvatar, setDraftAvatar] = useState<AvatarId>(user?.avatarId ?? "aurora");
  const [draftTheme, setDraftTheme] = useState<CardTheme>(user?.cardTheme ?? "classic");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [passwordDone, setPasswordDone] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [action, setAction] = useState<ProfileAction | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoDone, setPhotoDone] = useState<"saved" | "removed" | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const photoTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [nextStats, nextHistory] = await Promise.all([getStats(), getGameHistory(20)]);
        if (cancelled) return;
        setStats(nextStats);
        setHistory(nextHistory);
      } catch {
        if (!cancelled) setDataFailed(true);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;

  const minPassword = serverConfig?.minPasswordLength ?? DEFAULT_MIN_PASSWORD;
  const avatars = serverConfig?.avatars ?? AVATAR_IDS;
  const themes = serverConfig?.cardThemes ?? CARD_THEMES;
  const failure = error && action !== null ? describeFailure(action, error) : null;
  const photoEnabled = serverConfig?.avatarPhotoMaxBytes !== undefined;
  const hasPhoto = user.avatarPhoto !== undefined;
  const photoFailure = photoError ?? (action === "photo" ? failure : null);
  const photoPending = pending === "profile" && action === "photo";
  const trimmedName = displayName.trim();
  const nameValid = trimmedName.length >= 1 && trimmedName.length <= 24;
  const nameChanged = trimmedName !== user.displayName;
  const passwordValid =
    currentPassword.length > 0 && newPassword.length >= minPassword && confirmation === newPassword;

  const copyHandle = async () => {
    try {
      await navigator.clipboard.writeText(user.handle);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_800);
    } catch {
      setCopied(false);
    }
  };

  const start = (next: ProfileAction) => {
    dismissError();
    setAction(next);
  };

  const submitName = async (event: FormEvent) => {
    event.preventDefault();
    if (!nameValid || !nameChanged || pending !== null) return;
    start("identity");
    await updateProfile({ displayName: trimmedName });
  };

  const pickAvatar = async (avatarId: AvatarId) => {
    if ((avatarId === draftAvatar && !hasPhoto) || pending !== null) return;
    start("appearance");
    setDraftAvatar(avatarId);
    // Un dégradé choisi pendant qu'une photo le recouvre ne se verrait nulle
    // part : le choisir vaut donc renoncer à la photo.
    if (hasPhoto && !(await removeAvatarPhoto())) {
      setDraftAvatar(user.avatarId);
      return;
    }
    const ok = await updateProfile({ avatarId });
    if (!ok) setDraftAvatar(user.avatarId);
  };

  const choosePhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // Vidé aussitôt : sans cela, rechoisir le même fichier n'émettrait pas
    // d'événement et le bouton resterait sans effet.
    event.target.value = "";
    setPhotoDone(null);
    setPhotoError(null);
    if (file === null) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("Ce fichier n’est pas une image. Choisis une photo.");
      return;
    }
    start("photo");
    setPhotoFile(file);
  };

  const sendPhoto = async (photo: Blob) => {
    const limit = serverConfig?.avatarPhotoMaxBytes;
    if (limit !== undefined && photo.size > limit) {
      setPhotoFile(null);
      setPhotoError("Cette photo est trop lourde. Choisis une image plus légère.");
      return;
    }
    start("photo");
    const ok = await uploadAvatarPhoto(photo);
    setPhotoFile(null);
    if (ok) setPhotoDone("saved");
    photoTriggerRef.current?.focus();
  };

  const dropPhoto = async () => {
    if (pending !== null) return;
    start("photo");
    setPhotoDone(null);
    setPhotoError(null);
    if (await removeAvatarPhoto()) setPhotoDone("removed");
  };

  const pickTheme = async (cardTheme: CardTheme) => {
    if (cardTheme === draftTheme || pending !== null) return;
    start("appearance");
    setDraftTheme(cardTheme);
    const ok = await updateProfile({ cardTheme });
    if (!ok) setDraftTheme(user.cardTheme);
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!passwordValid || pending !== null) return;
    start("password");
    setPasswordDone(false);
    const ok = await changePassword({ currentPassword, newPassword });
    if (!ok) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmation("");
    setPasswordDone(true);
  };

  const submitDeletion = async (event: FormEvent) => {
    event.preventDefault();
    if (deletePassword.length === 0 || pending !== null) return;
    start("delete");
    await deleteAccount(deletePassword);
  };

  return (
    <main className="home-shell profile-shell">
      {/* Même barre que le salon : retour à gauche, marque au centre. */}
      <header className="app-header">
        <button className="ghost-button ghost-button--compact" type="button" onClick={onClose}>
          <ArrowLeft size={17} /> <span>Retour</span>
        </button>
        <div className="app-header__brand"><Brand compact /></div>
      </header>

      <motion.div
        className="profile-layout"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <section className="profile-identity glass-panel">
          <div className="join-panel__halo" aria-hidden="true" />
          <Avatar profile={{ ...user, avatarId: draftAvatar }} size="lg" />
          <div className="profile-identity__copy">
            <span className="eyebrow"><Sparkles size={15} aria-hidden="true" /> Ton compte</span>
            <h1>{user.displayName}</h1>
            <p>Membre depuis le {formatDate(user.createdAt, longDate)}</p>
          </div>
          <button
            className="handle-copy"
            type="button"
            onClick={() => void copyHandle()}
            aria-label={`Copier ton identifiant ${user.handle}`}
          >
            <span>{user.handle}</span>
            {copied ? <Check size={15} /> : <Clipboard size={15} />}
          </button>
        </section>

        <section className="profile-block glass-panel" aria-labelledby="profile-stats-title">
          <div className="profile-block__head">
            <span className="section-kicker"><Trophy size={14} /> Statistiques</span>
            <h2 id="profile-stats-title">Ton tableau de chasse</h2>
          </div>
          {loadingData ? (
            <p className="social-hint" role="status">Chargement de tes statistiques…</p>
          ) : dataFailed || !stats ? (
            <p className="social-alert" role="status">
              Impossible de récupérer tes statistiques pour l’instant. Réessaie dans un moment.
            </p>
          ) : (
            <ul className="stat-tiles">
              <StatTile icon={<Layers3 size={17} />} label="Parties jouées" value={String(stats.gamesPlayed)} />
              <StatTile icon={<Trophy size={17} />} label="Victoires" value={String(stats.gamesWon)} />
              <StatTile
                icon={<Percent size={17} />}
                label="Taux de victoire"
                value={`${Math.round(stats.winRate * 100)} %`}
              />
              <StatTile
                icon={<TrendingDown size={17} />}
                label="Meilleur score"
                value={stats.bestScore === null ? "—" : String(stats.bestScore)}
                hint="Le plus bas gagne"
              />
              <StatTile
                icon={<Target size={17} />}
                label="Score moyen"
                value={
                  stats.averageScore === null
                    ? "—"
                    : stats.averageScore.toLocaleString("fr-FR", { maximumFractionDigits: 1 })
                }
              />
              <StatTile icon={<Medal size={17} />} label="Manches jouées" value={String(stats.totalRounds)} />
              <StatTile icon={<Flame size={17} />} label="Série en cours" value={String(stats.currentStreak)} />
              <StatTile icon={<Flame size={17} />} label="Meilleure série" value={String(stats.bestStreak)} />
            </ul>
          )}
        </section>

        <section className="profile-block glass-panel" aria-labelledby="profile-history-title">
          <div className="profile-block__head">
            <span className="section-kicker"><History size={14} /> Historique</span>
            <h2 id="profile-history-title">Tes vingt dernières parties</h2>
          </div>
          {loadingData ? (
            <p className="social-hint" role="status">Chargement de ton historique…</p>
          ) : history.length === 0 ? (
            <div className="social-empty">
              <span className="social-empty__icon" aria-hidden="true"><History size={20} /></span>
              <strong>Aucune partie terminée</strong>
              <p>Joue une partie jusqu’au bout : elle apparaîtra ici avec ton classement et ton score.</p>
            </div>
          ) : (
            <ul className="history-list">
              {history.map((entry) => (
                <li className={`history-row${entry.won ? " is-won" : ""}`} key={entry.id}>
                  <span className="history-row__date">
                    <strong>{formatDate(entry.playedAt, shortDate)}</strong>
                    <small>Table {entry.roomCode}</small>
                  </span>
                  <span className="history-row__rank">
                    <strong>{placementLabel(entry.placement)}</strong>
                    <small>sur {entry.playerCount}</small>
                  </span>
                  <span className="history-row__score">
                    <strong>{entry.score}</strong>
                    <small>{entry.rounds} manche{entry.rounds > 1 ? "s" : ""}</small>
                  </span>
                  <span className="history-row__opponents">
                    {entry.opponents.length === 0
                      ? "—"
                      : entry.opponents.map((opponent) => opponent.name).join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="profile-block glass-panel" aria-labelledby="profile-look-title">
          <div className="profile-block__head">
            <span className="section-kicker"><Palette size={14} /> Personnalisation</span>
            <h2 id="profile-look-title">Ton style à la table</h2>
          </div>

          {photoEnabled && (
            <>
              <span className="field-label">Photo de profil</span>
              <div className="avatar-upload">
                <Avatar profile={{ ...user, avatarId: draftAvatar }} size="lg" />
                <div className="avatar-upload__copy">
                  <div className="avatar-upload__actions">
                    <button
                      ref={photoTriggerRef}
                      className="secondary-button"
                      type="button"
                      disabled={pending !== null}
                      onClick={() => fileRef.current?.click()}
                    >
                      <Camera size={16} />
                      {hasPhoto ? "Changer ma photo" : "Ajouter une photo"}
                    </button>
                    {hasPhoto && (
                      <button
                        className="secondary-button secondary-button--danger"
                        type="button"
                        disabled={pending !== null}
                        onClick={() => void dropPhoto()}
                      >
                        {/* La boîte de recadrage porte déjà l'état d'envoi : ici,
                            seul le retrait a besoin de son propre témoin. */}
                        {photoPending && photoFile === null ? (
                          <span className="button-spinner" aria-hidden="true" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                        Retirer ma photo
                      </button>
                    )}
                  </div>
                  <p className="social-hint">
                    Prends une photo ou choisis-en une dans ta galerie. Tu la recadres ici même,
                    et seul le carré retenu quitte ton appareil.
                  </p>
                </div>
                {/* Un vrai bouton pilote ce champ : `accept` sans `capture` laisse
                    le téléphone proposer l'appareil photo comme la galerie, alors
                    que `capture` imposerait l'appareil et fermerait la galerie. */}
                <input
                  ref={fileRef}
                  className="avatar-upload__input"
                  id="profile-photo-file"
                  type="file"
                  accept="image/*"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={choosePhoto}
                />
              </div>
              {photoFailure && <p className="social-alert" role="alert">{photoFailure}</p>}
              {photoDone !== null && (
                <p className="social-success" role="status">
                  {photoDone === "saved"
                    ? "Ta photo de profil est à jour."
                    : "Photo retirée : ton avatar reprend la main."}
                </p>
              )}
            </>
          )}

          <span className="field-label avatar-picker__label">Avatar</span>
          <ul className="avatar-picker">
            {avatars.map((avatarId) => (
              <li key={avatarId}>
                <button
                  className={`avatar-picker__choice${draftAvatar === avatarId && !hasPhoto ? " is-active" : ""}`}
                  type="button"
                  onClick={() => void pickAvatar(avatarId)}
                  aria-pressed={draftAvatar === avatarId && !hasPhoto}
                  aria-label={`Choisir l’avatar ${avatarLabels[avatarId]}`}
                  title={avatarLabels[avatarId]}
                >
                  {/* La vignette montre le dégradé seul : la photo le recouvrirait
                      et les huit choix se ressembleraient. */}
                  <Avatar profile={{ ...user, avatarId, avatarPhoto: undefined }} size="md" />
                </button>
              </li>
            ))}
          </ul>

          <span className="field-label theme-picker__label">Dos et couleurs des cartes</span>
          <ul className="theme-picker">
            {themes.map((cardTheme) => (
              <li key={cardTheme}>
                <button
                  className={`theme-picker__choice${draftTheme === cardTheme ? " is-active" : ""}`}
                  type="button"
                  onClick={() => void pickTheme(cardTheme)}
                  aria-pressed={draftTheme === cardTheme}
                  aria-label={`Choisir le thème ${themeLabels[cardTheme]}`}
                >
                  <span className={`theme-swatch theme-swatch--${cardTheme}`} aria-hidden="true">
                    <i /><i /><i />
                  </span>
                  {themeLabels[cardTheme]}
                </button>
              </li>
            ))}
          </ul>
          {action === "appearance" && failure && <p className="social-alert" role="alert">{failure}</p>}

          <form className="profile-form" onSubmit={(event) => void submitName(event)} noValidate>
            <label className="field-label" htmlFor="profile-name">Pseudo affiché</label>
            <div className={`text-field${displayName.length > 0 && !nameValid ? " is-invalid" : ""}`}>
              <span className="text-field__sigil" aria-hidden="true"><UserRound size={17} /></span>
              <input
                id="profile-name"
                autoComplete="username"
                maxLength={24}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
              <span className="text-field__count" aria-hidden="true">{displayName.length}/24</span>
            </div>
            <p className="social-hint">
              Ton numéro <b>#{user.discriminator}</b> ne change pas : tes amis te retrouveront toujours.
            </p>
            {action === "identity" && failure && <p className="field-error" role="alert">{failure}</p>}
            <button
              className="secondary-button"
              type="submit"
              disabled={!nameValid || !nameChanged || pending !== null}
            >
              {pending === "profile" && action === "identity" ? (
                <span className="button-spinner" aria-hidden="true" />
              ) : (
                <Check size={16} />
              )}
              Enregistrer le pseudo
            </button>
          </form>
        </section>

        <section className="profile-block glass-panel" aria-labelledby="profile-security-title">
          <div className="profile-block__head">
            <span className="section-kicker"><KeyRound size={14} /> Sécurité</span>
            <h2 id="profile-security-title">Mot de passe</h2>
          </div>

          <form className="profile-form" onSubmit={(event) => void submitPassword(event)} noValidate>
            <label className="field-label" htmlFor="profile-current-password">Mot de passe actuel</label>
            <div className="text-field">
              <span className="text-field__sigil" aria-hidden="true"><KeyRound size={17} /></span>
              <input
                id="profile-current-password"
                type="password"
                autoComplete="current-password"
                maxLength={128}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </div>

            <label className="field-label" htmlFor="profile-new-password">Nouveau mot de passe</label>
            <div className="text-field">
              <span className="text-field__sigil" aria-hidden="true"><KeyRound size={17} /></span>
              <input
                id="profile-new-password"
                type="password"
                autoComplete="new-password"
                maxLength={128}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder={`Au moins ${minPassword} caractères`}
              />
            </div>

            <label className="field-label" htmlFor="profile-confirm-password">Confirme le nouveau</label>
            <div
              className={`text-field${confirmation.length > 0 && confirmation !== newPassword ? " is-invalid" : ""}`}
            >
              <span className="text-field__sigil" aria-hidden="true"><KeyRound size={17} /></span>
              <input
                id="profile-confirm-password"
                type="password"
                autoComplete="new-password"
                maxLength={128}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </div>
            {confirmation.length > 0 && confirmation !== newPassword && (
              <p className="field-error">Les deux mots de passe diffèrent.</p>
            )}
            {action === "password" && failure && <p className="field-error" role="alert">{failure}</p>}
            {passwordDone && (
              <p className="social-success" role="status">
                Mot de passe mis à jour. Tes autres appareils ont été déconnectés.
              </p>
            )}
            <button className="secondary-button" type="submit" disabled={!passwordValid || pending !== null}>
              {pending === "password" ? (
                <span className="button-spinner" aria-hidden="true" />
              ) : (
                <KeyRound size={16} />
              )}
              Changer mon mot de passe
            </button>
          </form>
        </section>

        <section className="profile-block profile-block--danger glass-panel" aria-labelledby="profile-danger-title">
          <div className="profile-block__head">
            <span className="section-kicker section-kicker--danger"><ShieldAlert size={14} /> Zone sensible</span>
            <h2 id="profile-danger-title">Supprimer mon compte</h2>
          </div>
          <p className="social-hint">
            Ton compte, tes amis et ton historique seront effacés définitivement. Les parties en cours
            ne sont pas interrompues.
          </p>

          {deleteOpen ? (
            <form className="profile-form" onSubmit={(event) => void submitDeletion(event)} noValidate>
              <label className="field-label" htmlFor="profile-delete-password">
                Confirme avec ton mot de passe
              </label>
              <div className="text-field">
                <span className="text-field__sigil" aria-hidden="true"><KeyRound size={17} /></span>
                <input
                  id="profile-delete-password"
                  type="password"
                  autoComplete="current-password"
                  maxLength={128}
                  value={deletePassword}
                  onChange={(event) => setDeletePassword(event.target.value)}
                />
              </div>
              {action === "delete" && failure && <p className="field-error" role="alert">{failure}</p>}
              <div className="profile-form__row">
                <button
                  className="secondary-button secondary-button--danger"
                  type="submit"
                  disabled={deletePassword.length === 0 || pending !== null}
                >
                  {pending === "delete" ? (
                    <span className="button-spinner" aria-hidden="true" />
                  ) : (
                    <ShieldAlert size={16} />
                  )}
                  Supprimer définitivement
                </button>
                <button
                  className="ghost-button ghost-button--compact"
                  type="button"
                  onClick={() => {
                    setDeleteOpen(false);
                    setDeletePassword("");
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          ) : (
            <button
              className="secondary-button secondary-button--danger"
              type="button"
              onClick={() => {
                dismissError();
                setDeleteOpen(true);
              }}
            >
              <ShieldAlert size={16} /> Supprimer mon compte
            </button>
          )}
        </section>
      </motion.div>

      <AnimatePresence>
        {photoFile !== null && (
          <AvatarCropper
            file={photoFile}
            sending={photoPending}
            onCancel={() => {
              setPhotoFile(null);
              photoTriggerRef.current?.focus();
            }}
            onConfirm={(photo) => void sendPhoto(photo)}
            onFailure={(message) => {
              setPhotoFile(null);
              setPhotoError(message);
              photoTriggerRef.current?.focus();
            }}
          />
        )}
      </AnimatePresence>
    </main>
  );
};
